/**
 * PublicInquiryCTA — Phase 2D
 * ─────────────────────────────────────────────────────
 * A tiny, category-adaptive CTA + modal on the public company profile
 * that lets a logged-in user submit a request/lead directly to the
 * business. Uses the existing /platform/requests endpoint.
 */
import React, { useState } from "react";
import { Link } from "react-router-dom";
import { X, Send, Loader2, HandCoins, Shield, Wrench, Car, Package, Truck, Sparkles, CreditCard } from "lucide-react";
import { toast } from "sonner";
import { apiClient } from "../../lib/api";
import { useAuth } from "../../lib/auth";
import { useI18n } from "../../lib/i18n";

/* Map every business category to the most relevant inquiry kind + copy. */
export const CATEGORY_INQUIRIES = {
  bank:                { kind: "finance_inquiry",      icon: HandCoins,   ar_cta: "اطلب تمويلاً",       en_cta: "Apply for financing" },
  finance_company:     { kind: "finance_inquiry",      icon: HandCoins,   ar_cta: "اطلب تمويلاً",       en_cta: "Apply for financing" },
  financing_platform:  { kind: "finance_inquiry",      icon: HandCoins,   ar_cta: "اطلب تمويلاً",       en_cta: "Apply for financing" },
  fintech:             { kind: "finance_inquiry",      icon: HandCoins,   ar_cta: "اطلب استشارة مالية",  en_cta: "Request consultation" },
  bnpl_service:        { kind: "bnpl_application",     icon: CreditCard,  ar_cta: "اطلب خطة تقسيط",     en_cta: "Apply for BNPL" },
  leasing_company:     { kind: "leasing_inquiry",      icon: HandCoins,   ar_cta: "اطلب تأجيرًا تمويليًا", en_cta: "Request leasing" },
  insurance_company:   { kind: "insurance_quote",      icon: Shield,      ar_cta: "اطلب عرض تأمين",     en_cta: "Request insurance quote" },
  insurance_broker:    { kind: "insurance_quote",      icon: Shield,      ar_cta: "اطلب عرض تأمين",     en_cta: "Request insurance quote" },
  payment_provider:    { kind: "payment_solution_inquiry", icon: CreditCard, ar_cta: "استفسر عن حلول الدفع", en_cta: "Enquire about payments" },
  workshop:            { kind: "service",              icon: Wrench,      ar_cta: "احجز موعد صيانة",    en_cta: "Book a service" },
  car_wash:            { kind: "detailing_booking",    icon: Sparkles,    ar_cta: "احجز غسيلًا/تلميعًا",  en_cta: "Book a wash/detail" },
  inspection_center:   { kind: "inspection",           icon: Wrench,      ar_cta: "احجز فحصًا",         en_cta: "Book an inspection" },
  periodic_inspection: { kind: "inspection",           icon: Wrench,      ar_cta: "احجز فحصًا دوريًا",   en_cta: "Book a periodic inspection" },
  recovery_towing:     { kind: "recovery_request",     icon: Truck,       ar_cta: "اطلب سطحة",          en_cta: "Request recovery" },
  transport_company:   { kind: "transport_quote",      icon: Truck,       ar_cta: "اطلب عرض نقل",       en_cta: "Request transport quote" },
  tire_battery_shop:   { kind: "part_inquiry",         icon: Package,     ar_cta: "استفسر عن قطعة",     en_cta: "Enquire about a part" },
  parts_store:         { kind: "part_inquiry",         icon: Package,     ar_cta: "استفسر عن قطعة",     en_cta: "Enquire about a part" },
  salvage:             { kind: "part_inquiry",         icon: Package,     ar_cta: "استفسر عن قطعة",     en_cta: "Enquire about a part" },
  rental_company:      { kind: "rental_reservation",   icon: Car,         ar_cta: "اطلب حجز إيجار",     en_cta: "Reserve a rental" },
  dealer:              { kind: "purchase",             icon: Car,         ar_cta: "استفسر عن مركبة",    en_cta: "Enquire about a car" },
  official_agency:     { kind: "purchase",             icon: Car,         ar_cta: "استفسر عن مركبة",    en_cta: "Enquire about a car" },
  auction_house:       { kind: "purchase",             icon: Car,         ar_cta: "استفسر",             en_cta: "Enquire" },
  individual:          { kind: "other",                icon: Send,        ar_cta: "تواصل",              en_cta: "Contact" },
  other:               { kind: "other",                icon: Send,        ar_cta: "تواصل",              en_cta: "Contact" },
};


export default function PublicInquiryCTA({ business }) {
  const { lang } = useI18n();
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ subject: "", body: "", customer_phone: "" });
  const [busy, setBusy] = useState(false);

  if (!business) return null;
  const meta = CATEGORY_INQUIRIES[business.category] || CATEGORY_INQUIRIES.other;
  const Icon = meta.icon;

  const submit = async (e) => {
    e.preventDefault();
    if (!user) return;
    if (!form.subject.trim() && !form.body.trim()) {
      toast.error(lang === "ar" ? "أضِف موضوعًا أو تفاصيل" : "Add a subject or details");
      return;
    }
    setBusy(true);
    try {
      await apiClient.post("/platform/comms/requests", {
        kind: meta.kind,
        business_id: business.id,
        subject: form.subject.trim() || (lang === "ar" ? meta.ar_cta : meta.en_cta),
        body: form.body.trim() || null,
        customer_phone: form.customer_phone.trim() || null,
      });
      toast.success(lang === "ar" ? "تم إرسال طلبك. سيتواصل معك النشاط قريبًا." : "Request sent. The business will get in touch soon.");
      setForm({ subject: "", body: "", customer_phone: "" });
      setOpen(false);
    } catch (err) {
      const d = err?.response?.data?.detail;
      toast.error((typeof d === "object" ? (lang === "ar" ? d.ar : d.en) : d) || (lang === "ar" ? "فشل الإرسال" : "Failed"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="w-full inline-flex items-center justify-center gap-2 rounded-full h-11 text-sm font-bold shadow-sm hover:scale-[1.01] transition-transform"
        style={{ background: "linear-gradient(135deg,#e8c873,#c9a55c)", color: "#0a0a0a" }}
        data-testid="public-inquiry-cta"
      >
        <Icon className="w-4 h-4" />
        {lang === "ar" ? meta.ar_cta : meta.en_cta}
      </button>

      {open && (
        <div className="fixed inset-0 z-[100]" data-testid="public-inquiry-modal">
          <button type="button" aria-label="close" onClick={() => setOpen(false)}
                  className="absolute inset-0 bg-black/50 backdrop-blur-sm" />
          <div className="absolute inset-x-4 top-16 sm:inset-x-auto sm:left-1/2 sm:-translate-x-1/2 sm:top-24 sm:w-[440px] rounded-2xl bg-white shadow-2xl overflow-hidden">
            <div className="p-5 flex items-start justify-between border-b border-[#0a0a0a]/[0.06]">
              <div>
                <div className="text-[11px] uppercase tracking-[0.2em] text-[#8a6f2e] font-bold">
                  {lang === "ar" ? "طلب جديد" : "New request"}
                </div>
                <h3 className="text-lg font-display font-bold text-[#0a0a0a] mt-1">
                  {lang === "ar" ? meta.ar_cta : meta.en_cta}
                </h3>
                <div className="text-[11px] text-[#8a8a8a] mt-1">
                  {lang === "ar" ? `إلى: ${business.name_ar}` : `To: ${business.name_en || business.name_ar}`}
                </div>
              </div>
              <button type="button" onClick={() => setOpen(false)} className="text-[#8a8a8a] hover:text-[#0a0a0a]">
                <X className="w-5 h-5" />
              </button>
            </div>

            {!user ? (
              <div className="p-6 text-center">
                <div className="text-sm text-[#525252] mb-4">
                  {lang === "ar" ? "تسجيل الدخول مطلوب لإرسال طلب." : "You need to log in to send a request."}
                </div>
                <div className="flex gap-2 justify-center">
                  <Link
                    to={`/login?next=/company/${business.slug}`}
                    className="inline-flex items-center gap-2 rounded-full h-10 px-5 text-sm font-bold text-white bg-[#0a0a0a]"
                    onClick={() => setOpen(false)}
                  >
                    {lang === "ar" ? "تسجيل الدخول" : "Log in"}
                  </Link>
                  <Link
                    to={`/register?next=/company/${business.slug}`}
                    className="inline-flex items-center gap-2 rounded-full h-10 px-5 text-sm font-bold"
                    style={{ background: "linear-gradient(135deg,#e8c873,#c9a55c)", color: "#0a0a0a" }}
                    onClick={() => setOpen(false)}
                  >
                    {lang === "ar" ? "إنشاء حساب" : "Register"}
                  </Link>
                </div>
              </div>
            ) : (
              <form onSubmit={submit} className="p-5 space-y-3">
                <label className="block">
                  <div className="text-[11px] font-bold text-[#8a6f2e] uppercase tracking-widest mb-1">
                    {lang === "ar" ? "الموضوع" : "Subject"}
                  </div>
                  <input value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })}
                         placeholder={lang === "ar" ? meta.ar_cta : meta.en_cta}
                         className="w-full px-3 py-2 rounded-lg border border-[#0a0a0a]/15 text-sm"
                         data-testid="inquiry-subject" />
                </label>
                <label className="block">
                  <div className="text-[11px] font-bold text-[#8a6f2e] uppercase tracking-widest mb-1">
                    {lang === "ar" ? "التفاصيل" : "Details"}
                  </div>
                  <textarea rows={4} value={form.body} onChange={(e) => setForm({ ...form, body: e.target.value })}
                            maxLength={2000}
                            placeholder={lang === "ar" ? "اكتب تفاصيل طلبك…" : "Describe your request…"}
                            className="w-full px-3 py-2 rounded-lg border border-[#0a0a0a]/15 text-sm resize-none"
                            data-testid="inquiry-body" />
                </label>
                <label className="block">
                  <div className="text-[11px] font-bold text-[#8a6f2e] uppercase tracking-widest mb-1">
                    {lang === "ar" ? "الجوال (اختياري)" : "Phone (optional)"}
                  </div>
                  <input type="tel" value={form.customer_phone} onChange={(e) => setForm({ ...form, customer_phone: e.target.value })}
                         placeholder="05xxxxxxxx" dir="ltr"
                         className="w-full px-3 py-2 rounded-lg border border-[#0a0a0a]/15 text-sm"
                         data-testid="inquiry-phone" />
                </label>
                <div className="flex justify-end gap-2 pt-1">
                  <button type="button" onClick={() => setOpen(false)}
                          className="px-4 py-2 rounded-full text-sm text-[#525252]">
                    {lang === "ar" ? "إلغاء" : "Cancel"}
                  </button>
                  <button type="submit" disabled={busy}
                          className="inline-flex items-center gap-2 px-5 py-2 rounded-full text-sm font-bold shadow-sm disabled:opacity-50"
                          style={{ background: "linear-gradient(135deg,#e8c873,#c9a55c)", color: "#0a0a0a" }}
                          data-testid="inquiry-submit">
                    {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                    {lang === "ar" ? "إرسال" : "Send"}
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      )}
    </>
  );
}
